import { useEffect, useState } from 'react';

import { AxiosResponse } from 'axios';
import { isNil } from 'ramda';

import { showSnackbar } from '../index';
import useForceRefresh from './useForceRefresh';

interface UseResourceProps<T extends AxiosResponse> {
  fetcher: () => Promise<T>;
  transformer?: (response: T['data']) => any;
  errorMessage?: string;
  onError?: (error: any) => void;
  shouldFetch?: boolean;
}

const useResource = <T extends AxiosResponse>({
  fetcher,
  errorMessage,
  onError,
  transformer,
  shouldFetch = true
}: UseResourceProps<T>) => {
  const [resource, setResource] = useState<T['data'] | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const { timestamp, refresh } = useForceRefresh();

  useEffect(() => {
    if (shouldFetch) {
      setIsFetching(true);
      fetcher()
        .then((response) => {
          if (!isNil(transformer)) {
            const dataToSet = transformer(response.data);
            setResource(dataToSet);
          } else {
            setResource(response.data);
          }
        })
        .finally(() => setIsFetching(false))
        .catch((error) => {
          onError && onError(error);
          showSnackbar({
            severity: 'error',
            message: !isNil(errorMessage) ? errorMessage : 'Error'
          });
        });
    }
  }, [timestamp, shouldFetch]);

  return { resource, refresh, isFetching };
};

export default useResource;
